import React from 'react';
import { NumericFormat } from 'react-number-format';
import TipoParteSelector from '../tipoparteselector';
import { Unidade, TiposCache, inputStyle, primaryButtonStyle, secondaryButtonStyle } from './types';
import { TipoParteEmpreendimento } from '../../../../types/empreendimento';

interface UnidadeEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    unidadeTempData: Unidade | null;
    handleUnidadeTempChange: (field: string, value: any) => void;
    handleAtributoExtraTempChange: (key: string, value: any) => void;
    selectedUnidade: { type: 'torre' | 'avulsa'; torreIndex?: number; andar?: number; unidadeIndex: number } | null;
    torres: { nome: string }[];
    tiposCache: Record<number, TipoParteEmpreendimento>;
}

const UnidadeEditorModal: React.FC<UnidadeEditorModalProps> = ({
    isOpen,
    onClose,
    onSave,
    unidadeTempData,
    handleUnidadeTempChange,
    handleAtributoExtraTempChange,
    selectedUnidade,
    torres,
    tiposCache,
}) => {
    const isLote = Number(unidadeTempData?.tipo_produto) === 1;

    React.useEffect(() => {
        if (!unidadeTempData || !isLote) {
            return;
        }

        if (unidadeTempData.quantidade_garagem !== 0) {
            handleUnidadeTempChange('quantidade_garagem', 0);
        }

        if (unidadeTempData.vagas_rotativas !== 0) {
            handleUnidadeTempChange('vagas_rotativas', 0);
        }
    }, [
        isLote,
        unidadeTempData?.quantidade_garagem,
        unidadeTempData?.vagas_rotativas,
        handleUnidadeTempChange,
    ]);

    if (!isOpen || !unidadeTempData) {
        return null;
    }

    const renderAtributosExtrasTemp = () => {
        if (!unidadeTempData?.tipo_parte_empreendimento || unidadeTempData.tipo_parte_empreendimento.length === 0) {
            return null;
        }

        const consolidatedAtributos: Record<string, any> = {};
        unidadeTempData.tipo_parte_empreendimento.forEach(tipoId => {
            const tipo = tiposCache[Number(tipoId)];
            if (tipo && tipo.atributos_definidos) {
                Object.assign(consolidatedAtributos, tipo.atributos_definidos);
            }
        });

        return (
            <div className="mt-6 pt-4 border-t border-gray-200">
                <h5 className="text-md font-semibold text-gray-800 mb-4">
                    Atributos Específicos
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    {Object.entries(consolidatedAtributos).map(([key, attr]) => (
                        <div key={key}>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                {attr.label || key}
                                {attr.required && <span className="ml-1 text-red-500">*</span>}
                            </label>

                            {attr.type === 'text' && (
                                <input
                                    type="text"
                                    value={unidadeTempData?.atributos_extras?.[key] || ''}
                                    onChange={(e) => handleAtributoExtraTempChange(key, e.target.value)}
                                    className={inputStyle}
                                    placeholder={attr.placeholder || `Digite ${attr.label || key}`}
                                />
                            )}

                            {attr.type === 'number' && (
                                <input
                                    type="number"
                                    value={unidadeTempData?.atributos_extras?.[key] || ''}
                                    onChange={(e) => handleAtributoExtraTempChange(key, e.target.value)}
                                    className={inputStyle}
                                    placeholder={attr.placeholder || `Digite ${attr.label || key}`}
                                    min={attr.min}
                                    max={attr.max}
                                    step={attr.step}
                                />
                            )}

                            {attr.type === 'textarea' && (
                                <textarea
                                    value={unidadeTempData?.atributos_extras?.[key] || ''}
                                    onChange={(e) => handleAtributoExtraTempChange(key, e.target.value)}
                                    className={inputStyle}
                                    placeholder={attr.placeholder || `Digite ${attr.label || key}`}
                                    rows={3}
                                />
                            )}

                            {attr.type === 'select' && (
                                <select
                                    value={unidadeTempData?.atributos_extras?.[key] || ''}
                                    onChange={(e) => handleAtributoExtraTempChange(key, e.target.value)}
                                    className={inputStyle}
                                >
                                    <option value="" disabled>Selecione uma opção</option>
                                    {(attr.options || []).map((option: string) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            )}

                            {attr.type === 'checkbox' && (
                                <div className="mt-2">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={unidadeTempData?.atributos_extras?.[key] || false}
                                            onChange={(e) => handleAtributoExtraTempChange(key, e.target.checked)}
                                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            {attr.label || key}
                                        </span>
                                    </label>
                                </div>
                            )}

                            {attr.type === 'multiselect' && (
                                <select
                                    multiple
                                    value={unidadeTempData?.atributos_extras?.[key] || []}
                                    onChange={(e) => {
                                        const selectedValues = Array.from(e.target.selectedOptions).map(option => option.value);
                                        handleAtributoExtraTempChange(key, selectedValues);
                                    }}
                                    className={`${inputStyle} h-24`}
                                >
                                    {(attr.options || []).map((option: string) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {selectedUnidade?.type === 'torre'
                            ? `Editar Unidade no ${unidadeTempData.andar}º Andar (${torres[selectedUnidade.torreIndex!].nome})`
                            : `Editar Unidade Avulsa`
                        }
                    </h3>
                </div>
                <div className="p-6 overflow-y-auto">
                    {unidadeTempData.confirmada === false && (
                        <div className="mb-4 p-3 bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 rounded-r-lg">
                            <p className="font-bold">Esta unidade precisa de confirmação.</p>
                            <p className="text-sm">Por favor, verifique os dados e salve para confirmar a unidade.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <div className='md:col-span-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo da Unidade</label>
                            <select
                                value={unidadeTempData.tipo_produto || 2}
                                onChange={(e) => handleUnidadeTempChange('tipo_produto', parseInt(e.target.value))}
                                className={inputStyle}
                            >
                                <option value={1}>Lote</option>
                                <option value={2}>Apartamento</option>
                                <option value={3}>Comercial</option>
                                <option value={4}>Casa</option>
                                <option value={5}>Compacto</option>
                                <option value={6}>Empresarial</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Unidade *</label>
                            <input type="text" value={unidadeTempData.nome || ''} onChange={(e) => handleUnidadeTempChange('nome', e.target.value)} className={inputStyle} placeholder="Ex: Apartamento 2 quartos com suíte" />
                        </div>
                        <div>
                            <TipoParteSelector
                                label="Características da unidade"
                                value={unidadeTempData.tipo_parte_empreendimento || []}
                                onChange={(value) => handleUnidadeTempChange('tipo_parte_empreendimento', value)}
                            />
                        </div>
                        <div className="block text-sm font-medium text-gray-700 mb-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Disponibilidade</label>
                            <select
                                value={unidadeTempData.disponibilidade || 1}
                                onChange={(e) => handleUnidadeTempChange('disponibilidade', e.target.value)}
                                className={inputStyle}
                            >
                                <option value="1" disabled>Disponível</option>
                                <option value="1">Disponível</option>
                                <option value="2">Vendido</option>
                                <option value="3">Distratado</option>
                                <option value="4">Bloqueada</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Área Privativa (m²) *</label>
                            <input type="text" value={unidadeTempData.area_privativa_m2 || ''} onChange={(e) => handleUnidadeTempChange('area_privativa_m2', e.target.value)} className={inputStyle} placeholder="Ex: 75.50" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Imóvel *</label>
                             <NumericFormat
                                    value={unidadeTempData?.valor_imovel ?? ''}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    prefix="R$ "
                                    decimalScale={2}
                                    fixedDecimalScale
                                    allowNegative={false}
                                    className={inputStyle}
                                    placeholder="R$ 0,00"
                                    onValueChange={(values) => {
                                    handleUnidadeTempChange('valor_imovel', values.value);
                                    }}
                                />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Avaliação</label>
                             <NumericFormat
                                    value={unidadeTempData?.valor_avaliacao ?? ''}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    prefix="R$ "
                                    decimalScale={2}
                                    fixedDecimalScale
                                    allowNegative={false}
                                    className={inputStyle}
                                    placeholder="R$ 0,00"
                                    onValueChange={(values) => {
                                    handleUnidadeTempChange('valor_avaliacao', values.value);
                                    }}
                                />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento *</label>
                            <input type="text" value={unidadeTempData.forma_pagamento || ''} onChange={(e) => handleUnidadeTempChange('forma_pagamento', e.target.value)} className={inputStyle} placeholder="Ex: Financiamento, À vista" />
                        </div>
                        {!isLote && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade de Garagem *</label>
                                <input
                                    type="number"
                                    value={unidadeTempData.quantidade_garagem || ''}
                                    onChange={(e) => handleUnidadeTempChange('quantidade_garagem', e.target.value)}
                                    className={inputStyle}
                                    placeholder="Ex: 1"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ano de Disponibilidade</label>
                            <select
                                value={unidadeTempData.ano_disponibilidade}
                                onChange={(e) =>
                                    handleUnidadeTempChange('ano_disponibilidade', Number(e.target.value))
                                }
                                className={inputStyle}
                                >
                                {[...Array(10)].map((_, i) => {
                                    const ano = 2024 + i;
                                    return (
                                    <option key={ano} value={ano}>
                                        {ano}
                                    </option>
                                    );
                                })}
                                </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mês de Disponibilidade</label>
                            <select
                                value={unidadeTempData.mes_disponibilidade}
                                onChange={(e) =>
                                    handleUnidadeTempChange('mes_disponibilidade', Number(e.target.value))
                                }
                                className={inputStyle}
                                >
                                {[
                                    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                                    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                                ].map((mes, i) => (
                                    <option key={i + 1} value={i + 1}>
                                    {mes}
                                    </option>
                                ))}
                                </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Origem do Recurso</label>
                            <select value={unidadeTempData.origem_recurso || 1} onChange={(e) => handleUnidadeTempChange('origem_recurso', parseInt(e.target.value))} className={inputStyle}>
                                <option value={1}>Próprio</option>
                                <option value={2}>SFH</option>
                                <option value={3}>Condomínio</option>
                                <option value={4}>Cooperativa</option>
                                <option value={5}>Próprio + SFH</option>
                                <option value={6}>Outros</option>
                                <option value={7}>MCMV</option>
                            </select>
                        </div>
                        {!isLote && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade de vagas rotativas *</label>
                                <input
                                    type="number"
                                    value={unidadeTempData.vagas_rotativas || ''}
                                    onChange={(e) => handleUnidadeTempChange('vagas_rotativas', e.target.value)}
                                    className={inputStyle}
                                    placeholder="Ex: 1"
                                />
                            </div>
                        )}
                        {!isLote && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Vagas rotativas — fixa</label>
                                <input
                                    type="number"
                                    value={unidadeTempData.rotativa_fixa || ''}
                                    onChange={(e) => handleUnidadeTempChange('rotativa_fixa', e.target.value)}
                                    className={inputStyle}
                                    placeholder="Ex: 1"
                                    title="Vaga rotativa com posição fechada, dedicada a esta unidade"
                                />
                            </div>
                        )}
                        {!isLote && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Vagas rotativas — aleatória</label>
                                <input
                                    type="number"
                                    value={unidadeTempData.rotativa_aleatoria || ''}
                                    onChange={(e) => handleUnidadeTempChange('rotativa_aleatoria', e.target.value)}
                                    className={inputStyle}
                                    placeholder="Ex: 1"
                                    title="Vaga rotativa em pool compartilhado, sem posição fixa"
                                />
                            </div>
                        )}
                        <div className="flex items-center pt-2">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={unidadeTempData.mcmv || false}
                                    onChange={(e) => {
                                        const marcado = e.target.checked;
                                        handleUnidadeTempChange('mcmv', marcado);
                                        // Marcar MCMV já indica a origem do recurso — evita o usuário
                                        // marcar o checkbox e esquecer de trocar o select também.
                                        if (marcado) handleUnidadeTempChange('origem_recurso', 7);
                                    }}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-800 font-medium">Minha Casa Minha Vida</span>
                            </label>
                        </div>
                    </div>
                    {renderAtributosExtrasTemp()}
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-4">
                    <button type="button" onClick={onClose} className={secondaryButtonStyle}>Cancelar</button>
                    <button type="button" onClick={onSave} className={primaryButtonStyle}>
                        {unidadeTempData?.confirmada === false ? 'Confirmar e Salvar' : 'Salvar Unidade'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnidadeEditorModal;
